// RuleOverridesSection — per-apartment enable/disable toggle + optional template override.
// Shown on the rule edit screen.
// NOTE: To CLEAR a template override, omit template_id on PUT (the API does not accept
// explicit null — clear-via-omit is the supported path).

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apartmentsQuery, type Apartment } from '@/lib/apartments';
import {
  ruleOverridesQuery,
  putRuleOverride,
  deleteRuleOverride,
  type AutomationRuleOverride,
} from '@/lib/rules';
import { templatesQuery } from '@/lib/templates';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface RuleOverridesSectionProps {
  ruleId: string;
  disabled?: boolean;
}

// Template picker sub-dialog
function TemplatePicker({
  open,
  onClose,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (id: string) => void;
}) {
  const { data } = useQuery({ ...templatesQuery({ onlyActive: true }), enabled: open });
  const templates = data?.items ?? [];
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Override template"
      description="Pick a template override for this apartment."
    >
      <ul className="space-y-1" role="list">
        {templates.map((t) => (
          <li key={t.id}>
            <button
              type="button"
              onClick={() => onSelect(t.id)}
              className="w-full rounded-[var(--radius-control)] border border-[var(--border-hairline)] px-3 py-2.5 text-left text-sm font-medium hover:bg-[var(--surface-sunken)] transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-glow)]"
            >
              {t.name}
            </button>
          </li>
        ))}
        {templates.length === 0 && (
          <li>
            <p className="text-sm text-muted py-4 text-center">No templates.</p>
          </li>
        )}
      </ul>
    </Dialog>
  );
}

export function RuleOverridesSection({ ruleId, disabled }: RuleOverridesSectionProps) {
  const qc = useQueryClient();
  const [pickerApt, setPickerApt] = useState<string | null>(null);

  const { data: apartments, isLoading: aptsLoading } = useQuery(apartmentsQuery);
  const { data: overrides, isLoading: ovLoading } = useQuery(ruleOverridesQuery(ruleId));

  const overrideMap = new Map<string, AutomationRuleOverride>(
    (overrides ?? []).map((o) => [o.apartment_id, o]),
  );

  const putMut = useMutation({
    mutationFn: (vars: { apartmentId: string; enabled: boolean; templateId?: string }) =>
      putRuleOverride({
        throwOnError: true,
        path: { id: ruleId, apartment_id: vars.apartmentId },
        body: {
          enabled: vars.enabled,
          ...(vars.templateId !== undefined ? { template_id: vars.templateId } : {}),
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rules', ruleId, 'overrides'] }),
  });

  const delMut = useMutation({
    mutationFn: (apartmentId: string) =>
      deleteRuleOverride({
        throwOnError: true,
        path: { id: ruleId, apartment_id: apartmentId },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rules', ruleId, 'overrides'] }),
  });

  if (aptsLoading || ovLoading) {
    return (
      <div className="space-y-2">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} shape="rect" className="h-12 w-full" />
        ))}
      </div>
    );
  }

  const apts: Apartment[] = apartments ?? [];

  return (
    <div className="flex flex-col gap-2">
      {apts.length === 0 && <p className="text-sm text-muted">No apartments configured.</p>}
      {apts.map((apt) => {
        const override = overrideMap.get(apt.id);
        // If no override row: rule is effectively enabled for this apt (default on)
        const isEnabled = override ? override.enabled : true;
        const hasOverride = !!override;
        const templateOverride = override?.template_id;

        return (
          <div
            key={apt.id}
            className="flex flex-col gap-1.5 rounded-[var(--radius-control)] border border-[var(--border-soft)] px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between"
          >
            {/* Left: apt name + override badge */}
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate text-sm font-medium text-foreground">{apt.title}</span>
              {templateOverride && (
                <span className="text-xs text-muted">Template override set</span>
              )}
            </div>

            {/* Right: actions */}
            <div className="flex flex-shrink-0 items-center gap-2">
              {/* Enable/disable toggle */}
              <button
                type="button"
                role="switch"
                aria-checked={isEnabled}
                aria-label={`${isEnabled ? 'Disable' : 'Enable'} rule for ${apt.title}`}
                disabled={disabled || putMut.isPending || delMut.isPending}
                onClick={() => {
                  if (isEnabled && !hasOverride) {
                    putMut.mutate({ apartmentId: apt.id, enabled: false });
                  } else if (isEnabled && hasOverride) {
                    putMut.mutate({
                      apartmentId: apt.id,
                      enabled: false,
                      templateId: templateOverride,
                    });
                  } else if (!isEnabled && hasOverride) {
                    putMut.mutate({
                      apartmentId: apt.id,
                      enabled: true,
                      templateId: templateOverride,
                    });
                  } else {
                    // !isEnabled && !hasOverride: re-enable with no template override
                    putMut.mutate({ apartmentId: apt.id, enabled: true });
                  }
                }}
                className={cn(
                  'relative inline-flex h-5 w-9 cursor-pointer items-center rounded-full border-2 transition-colors',
                  'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-glow)]',
                  'disabled:cursor-not-allowed disabled:opacity-55',
                  isEnabled
                    ? 'border-[var(--ollio-navy)] bg-[var(--ollio-navy)]'
                    : 'border-[var(--border-soft)] bg-[var(--border-soft)]',
                )}
              >
                <span
                  className={cn(
                    'inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform',
                    isEnabled ? 'translate-x-4' : 'translate-x-0',
                  )}
                />
              </button>

              {/* Template override */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => setPickerApt(apt.id)}
              >
                {templateOverride ? 'Change template' : 'Override template'}
              </Button>

              {/* Clear override row */}
              {hasOverride && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={disabled || delMut.isPending}
                  onClick={() => delMut.mutate(apt.id)}
                >
                  Reset
                </Button>
              )}
            </div>
          </div>
        );
      })}

      {pickerApt && (
        <TemplatePicker
          open
          onClose={() => setPickerApt(null)}
          onSelect={(templateId) => {
            const aptId = pickerApt;
            const ov = overrideMap.get(aptId);
            const isCurrentlyEnabled = ov ? ov.enabled : true;
            putMut.mutate({ apartmentId: aptId, enabled: isCurrentlyEnabled, templateId });
            setPickerApt(null);
          }}
        />
      )}
    </div>
  );
}
